import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Google-Meet-style microphone permission + monitoring hook.
 *
 * - Detects the current permission state (Permissions API) on mount and, when
 *   already granted, starts monitoring automatically (returning candidates skip
 *   the button).
 * - `requestPermission()` is meant to be called from a user gesture ("Enable
 *   microphone") so Chrome's native popup appears intentionally.
 * - Runs a live level meter (Web Audio) and reports whether real audio input
 *   was detected.
 * - Cleans everything up (tracks, AudioContext, rAF, permission listener).
 *
 * Release guarantees (these matter more than they look):
 * - Every MediaStream and AudioContext the hook opens is registered, and
 *   `stopMonitoring()` closes ALL of them — never just the most recent one.
 * - `requestPermission()` is single-flight, and the permission-change listener
 *   never re-acquires while a request is pending or a stream is already live.
 * - Each mount cycle has its own cancellation flag, so a detect() that outlives
 *   its effect (StrictMode double-invoke, fast unmount) cannot act.
 * A microphone capture that outlives the lobby is invisible on desktop, where
 * captures share the device, but on Android it starves the system speech
 * recognizer: the interview's mic "opens" and receives only silence.
 */

export type MicPermissionState =
  | "unsupported"
  | "insecure"
  | "prompt"
  | "granted"
  | "denied"
  | "nodevice"
  | "error";

export interface UseMicrophonePermission {
  permissionState: MicPermissionState;
  isMicrophoneReady: boolean;
  audioInputDetected: boolean;
  audioLevel: number;
  deviceLabel: string;
  requestPermission: () => Promise<void>;
  retry: () => Promise<void>;
  stopMonitoring: () => void;
}

const SOUND_RMS_THRESHOLD = 8; // 0..128 time-domain RMS: ~speech vs silence
const LEVEL_UPDATE_MS = 80; // throttle meter re-renders (~12.5fps)

function isInsecureContext(): boolean {
  return (
    typeof window !== "undefined" &&
    !window.isSecureContext &&
    window.location.hostname !== "localhost"
  );
}

function stopTracks(stream: MediaStream): void {
  stream.getTracks().forEach((t) => {
    try {
      t.stop();
    } catch {
      /* noop */
    }
  });
}

export function useMicrophonePermission(): UseMicrophonePermission {
  const [permissionState, setPermissionState] = useState<MicPermissionState>("prompt");
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioInputDetected, setAudioInputDetected] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState("");

  // Registry of everything ever opened by this hook instance.
  const streamsRef = useRef<MediaStream[]>([]);
  const ctxsRef = useRef<AudioContext[]>([]);
  const rafRef = useRef<number | null>(null);
  const lastLevelTsRef = useRef(0);
  const mountedRef = useRef(true);
  // The pending requestPermission(), if any — a second call joins it.
  const inFlightRef = useRef<Promise<void> | null>(null);

  const hasLiveStream = () =>
    streamsRef.current.some((s) => s.getTracks().some((t) => t.readyState === "live"));

  const stopMonitoring = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    for (const ctx of ctxsRef.current.splice(0)) {
      try {
        void ctx.close();
      } catch {
        /* already closed */
      }
    }
    for (const stream of streamsRef.current.splice(0)) {
      stopTracks(stream);
    }
  }, []);

  const startMonitoring = useCallback((stream: MediaStream) => {
    const AudioCtx: typeof AudioContext | undefined =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    ctxsRef.current.push(ctx);
    void ctx.resume?.();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    const loop = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = data[i] - 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length); // 0..~128
      if (!mountedRef.current) return;
      if (rms > SOUND_RMS_THRESHOLD) setAudioInputDetected(true);
      const now = performance.now();
      if (now - lastLevelTsRef.current > LEVEL_UPDATE_MS) {
        lastLevelTsRef.current = now;
        setAudioLevel(Math.min(1, rms / 35));
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const requestPermission = useCallback(async () => {
    // Single-flight: never two getUserMedia() calls in the air at once.
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      stopMonitoring();
      setAudioInputDetected(false);
      setAudioLevel(0);

      if (isInsecureContext()) {
        setPermissionState("insecure");
        return;
      }
      const md = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
      if (!md?.getUserMedia) {
        setPermissionState("unsupported");
        return;
      }
      try {
        const stream = await md.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (!mountedRef.current) {
          // Resolved after the lobby went away — release on the spot.
          stopTracks(stream);
          return;
        }
        streamsRef.current.push(stream);
        const track = stream.getAudioTracks()[0];
        setDeviceLabel(track?.label || "Microphone");
        setPermissionState("granted");
        startMonitoring(stream);
      } catch (err) {
        const name = err instanceof DOMException ? err.name : "";
        if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
          setPermissionState("denied");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setPermissionState("nodevice");
        } else {
          setPermissionState("error");
        }
      }
    })();

    inFlightRef.current = run;
    try {
      await run;
    } finally {
      if (inFlightRef.current === run) inFlightRef.current = null;
    }
  }, [startMonitoring, stopMonitoring]);

  const retry = useCallback(async () => {
    await requestPermission();
  }, [requestPermission]);

  // Detect initial state; auto-monitor if already granted; react to changes.
  useEffect(() => {
    mountedRef.current = true;
    // Per-run flag: a detect() from a previous effect run must not act, even
    // though mountedRef may have been set back to true by the next run.
    let cancelled = false;
    let permStatus: PermissionStatus | null = null;

    const applyState = (state: PermissionState) => {
      if (cancelled || !mountedRef.current) return;
      if (state === "granted") {
        // The candidate just flipped the mic to Allow (via the popup or the
        // browser's site UI). Re-acquire only if nothing is pending or live —
        // a duplicate acquisition here is exactly the leak we must never make.
        if (!inFlightRef.current && !hasLiveStream()) void requestPermission();
      } else if (state === "denied") {
        setPermissionState("denied");
      } else {
        setPermissionState("prompt");
      }
    };

    const detect = async () => {
      if (isInsecureContext()) {
        setPermissionState("insecure");
        return;
      }
      const md = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
      if (!md?.getUserMedia) {
        setPermissionState("unsupported");
        return;
      }
      const perms = typeof navigator !== "undefined" ? navigator.permissions : undefined;
      if (perms?.query) {
        try {
          const status = await perms.query({ name: "microphone" as PermissionName });
          if (cancelled) return;
          permStatus = status;
          applyState(status.state);
          status.onchange = () => {
            if (!cancelled) applyState(status.state);
          };
          return;
        } catch {
          /* Permissions API doesn't support "microphone" (e.g. Firefox) — fall through */
        }
      }
      if (!cancelled) setPermissionState("prompt");
    };

    void detect();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (permStatus) permStatus.onchange = null;
      stopMonitoring();
    };
  }, [requestPermission, stopMonitoring]);

  return {
    permissionState,
    isMicrophoneReady: permissionState === "granted",
    audioInputDetected,
    audioLevel,
    deviceLabel,
    requestPermission,
    retry,
    stopMonitoring,
  };
}
